import { CircleCheckBig, Info, TriangleAlert } from 'lucide-react';

import { StreamingText } from '@/_shared/components/StreamingText';

type Severity = 'error' | 'warning' | 'info' | 'success';

interface AlertBlockProps {
  content: string;
  metadata?: unknown;
}

const SEVERITY_STYLES: Record<
  Severity,
  { border: string; bg: string; text: string; Icon: React.ComponentType<{ size?: number; className?: string }> }
> = {
  error: {
    border: 'border-red-200/50',
    bg: 'bg-red-50',
    text: 'text-red-700',
    Icon: TriangleAlert,
  },
  warning: {
    border: 'border-amber-200',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    Icon: TriangleAlert,
  },
  info: {
    border: 'border-blue-700/50',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    Icon: Info,
  },
  success: {
    border: 'border-green-700/50',
    bg: 'bg-green-50',
    text: 'text-green-700',
    Icon: CircleCheckBig,
  },
};

function getSeverity(metadata: unknown): Severity {
  if (
    metadata &&
    typeof metadata === 'object' &&
    'severity' in metadata &&
    typeof (metadata as { severity: unknown }).severity === 'string'
  ) {
    const s = (metadata as { severity: string }).severity as Severity;
    if (s in SEVERITY_STYLES) return s;
  }
  return 'info';
}

export function AlertBlock({ content, metadata }: AlertBlockProps): React.ReactElement {
  const severity = getSeverity(metadata);
  const { border, bg, text, Icon } = SEVERITY_STYLES[severity];

  return (
    <div
      className={`flex items-start gap-2 rounded-lg border ${border} ${bg} p-3 text-sm ${text}`}
      data-testid={`alert-${severity}`}
    >
      <Icon size={16} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <StreamingText content={content} />
      </div>
    </div>
  );
}
